import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useSearchResults} from '../contexts/SearchResultsContext'
import {useFeaturedListings} from '../contexts/FeaturedListingsContext'
import {useRecommendedListings} from '../contexts/RecommendedListingsContext'
import {useQuery} from '../contexts/QueryContext'
import {useCategory} from '../contexts/CategoryContext'
import {useSearchType} from '../contexts/SearchTypeContext'
import {useCopilotApp} from '../contexts/CopilotAppContext'
import {announce} from '@github-ui/aria-live'

interface FetchSearchResults {
  isSearching: boolean
  loading: boolean
  fetchSearchResults: (path: string) => Promise<void>
}

export function useFetchSearchResults(): FetchSearchResults {
  const {setSearchResults} = useSearchResults()
  const {setFeatured} = useFeaturedListings()
  const {setRecommended} = useRecommendedListings()
  const {query} = useQuery()
  const {category} = useCategory()
  const {type} = useSearchType()
  const {copilotApp} = useCopilotApp()
  const shouldDisplaySearchResults = useMemo(() => {
    return !!(query || category || type || copilotApp)
  }, [category, copilotApp, query, type])
  const [isSearching, setIsSearching] = useState(shouldDisplaySearchResults)
  const [loading, setLoading] = useState(false)
  const [lastLoadedPath, setLastLoadedPath] = useState<string | undefined>(undefined)
  const pendingAnnouncementTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const clearPendingAnnouncement = useCallback(() => {
    if (!pendingAnnouncementTimeoutRef.current) return

    clearTimeout(pendingAnnouncementTimeoutRef.current)
    pendingAnnouncementTimeoutRef.current = undefined
  }, [])

  useEffect(() => {
    return () => {
      clearPendingAnnouncement()
    }
  }, [clearPendingAnnouncement])

  const fetchSearchResults = useCallback(
    async (path: string) => {
      if (path === lastLoadedPath) return

      clearPendingAnnouncement()

      setLoading(true)
      const response = await verifiedFetchJSON(path)
      const data = await response.json()
      if (shouldDisplaySearchResults) {
        // This will be just search results
        setSearchResults(data)
        // Announce after a short delay so status messages are read after ActionMenu overlays close.
        // Immediate announcements can be dropped by screen readers when interactive overlays are active.
        pendingAnnouncementTimeoutRef.current = setTimeout(() => {
          announceSearchResults(data.total)
          pendingAnnouncementTimeoutRef.current = undefined
        }, 300)
      } else {
        // This will be the full index payload
        setFeatured(data.featured)
        setRecommended(data.recommended)
        setSearchResults(data.searchResults ?? data.results)
      }
      setIsSearching(shouldDisplaySearchResults)
      setLoading(false)
      setLastLoadedPath(path)
    },
    [
      lastLoadedPath,
      setFeatured,
      setRecommended,
      setSearchResults,
      shouldDisplaySearchResults,
      clearPendingAnnouncement,
    ],
  )

  return {fetchSearchResults, isSearching, loading}
}

function announceSearchResults(total: number) {
  if (total === 0) {
    // No results found
    announce('No results. Try searching by different keywords.')
  } else {
    // Results found
    announce(`${total} ${total === 1 ? 'result' : 'results.'}`)
  }
}
